import { _, helpers, React, ts, ui } from '_core';

import { percentColumns, transform } from './helpers';
import { Params } from './types';

type ChartProps = {
  id: string;
  readOnly: boolean;
  widgetKey: string;
  loadWidgetData: (_payload: ts.types.widgets.WidgetGetDataParams) => Promise<ts.types.widgets.WidgetGetDataResponse>;
  summary: string;
  params: Params;
  fullScreen: boolean;
  title: string;
  formOptions: { key: string; label: string }[];
};

const Chart = ({ id, readOnly, loadWidgetData, summary, fullScreen, params, title, formOptions }: ChartProps) => {
  const [data, setData] = React.useState<ts.types.widgets.TableData>(null);
  const [error, setError] = React.useState<ts.types.common.ApiError>(null);

  const loadData = async () => {
    try {
      const response = await loadWidgetData({
        data: {
          file: summary,
        },
      });

      if (response) {
        setData(response.data);
      }
    } catch (err) {
      setError(err as ts.types.common.ApiError);
    }
  };

  React.useEffect(() => {
    if (summary) loadData();
    return () => setData(null);
  }, [summary]);

  const currField = formOptions.find((opt) => opt.key == params.selectedParam);

  const tooltip = React.useMemo(() => {
    let tooltip = '';
    tooltip += `[Bold]${currField.label}:[/] {${currField.key}} \n`;
    if (percentColumns.includes(params.metric)) tooltip += `[Bold]Metric:[/] {${params.metric}_percent}%`;
    else tooltip += `[Bold]Metric:[/] {${params.metric}}`;
    return tooltip;
  }, [currField, params.metric]);

  const formattedData = React.useMemo(() => {
    if (!data) return null;
    return transform(data as ts.types.widgets.ScatterData, params.metric);
  }, [data, params.metric]);

  return (
    <ui.ScatterPlotChart
      id={id}
      fullScreen={fullScreen}
      readOnly={readOnly}
      data={formattedData}
      alert={error ? { type: 'error', message: helpers.parseApiError(error) } : undefined}
      xAxisField={currField.key}
      yAxisField={params.metric}
      lineSeriesName={_.startCase(params.selectedParam)}
      exportTitle={title}
      numberFormat="#.####"
      yAxisFormatter={percentColumns.includes(params.metric) ? '#.##%' : null}
      xAxisFormatter={null}
      hideLegend
      xAxisLabel={currField.label}
      yAxisLabel={_.startCase(params.metric)}
      elementTooltipText={tooltip}
    />
  );
};

export default Chart;
